import {
  Directive,
  ElementRef,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  Input,
  ViewContainerRef,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { IRole } from '../models/role';
import { App } from '../state/app.actions';
import { ApplicationState } from '../state/app.state';
import { IMemberResult } from '../models/member';
import { IOrganizationBase } from '../models/organization';
import { User } from '../users/user.actions';

@Directive({
  selector: '[menuPermission]',
})
export class MenuPermissionDirective implements OnInit, OnDestroy {
  @Input()
  public requiredPermission: string;
  @Input()
  public allowedPermissions: string[];

  @Select(ApplicationState.getRoles)
  roles$: Observable<Array<IRole>>;

  @Select(ApplicationState.getCurrentOrganization)
  currentOrganization$: Observable<IOrganizationBase>;

  unSubscribeAll$ = new Subject<void>();
  appRoles: IRole[] = undefined;
  userRoles: string[] = undefined;
  constructor(
    private viewContainer: ViewContainerRef,
    private elementRef: ElementRef,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.setValueFromToken();
    this.initSubscribers();
    this.appRoles = this.store.snapshot().application.roles as IRole[];
    if (this.store.snapshot().application.allRoles === undefined) {
      this.store.dispatch(new App.Actions.SetRoles());
    }

    this.updateVisibility();
  }
  ngOnDestroy(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }

  private initSubscribers(): void {
    this.roles$
      .pipe(
        takeUntil(this.unSubscribeAll$),
        tap(
          (roles) => {
            if (this.store.snapshot().application.allRoles === undefined) {
              this.store.dispatch(new App.Actions.SetRoles());
            }
          },
          (error) => console.log('initSubscribers getRoles error', error),
        ),
      )
      .subscribe((roles) => {
        this.appRoles = roles || [];
        this.updateVisibility();
      });

    this.currentOrganization$
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((organization) => {
        this.userRoles = organization?.roles || [];
        this.updateVisibility();
      });
  }

  private updateVisibility(): void {
    const permissions = this.requiredPermission
      ? [this.requiredPermission]
      : this.allowedPermissions;

    const hasPermission = this.hasAction(permissions);
    this.elementRef.nativeElement.style.display = hasPermission ? '' : 'none';
  }

  private hasAction(permissions: string[]): boolean {
    if (!permissions) return false;
    this.appRoles = this.store.snapshot().application.roles;

    if (!this.appRoles) return false;

    if (!this.userRoles) {
      this.userRoles = this.store.snapshot().application.currentOrganization
        ? this.store.snapshot().application.currentOrganization.roles
        : [];
    }
    if (!this.userRoles || this.userRoles.length === 0) return false;

    const rolesAndActions = this.appRoles.filter((currentRole) =>
      this.userRoles.includes(currentRole.role),
    );
    let actions = [].concat(
      ...rolesAndActions.map(({ actions }) => actions.map((o) => o)),
    );
    actions = [...new Set(actions)];
    return actions.some((permission) => {
      return permissions.includes(permission);
    }); // const appRoles = this.store.snapshot().application.roles;
  }

  setValueFromToken(): void {
    //Set Current Organization
    if (this.userRoles || !localStorage.getItem('organization')) return;
    // If NGXS already has a currentOrganization in state (e.g. during an org switch),
    // skip — the SetCurrentOrganization dispatch is already in flight.
    // Dispatching again here with stale localStorage.user.currentOrganization would
    // race against the real dispatch and overwrite state with the wrong org.
    if (this.store.snapshot().application.currentOrganization) return;
    //if (!this.userRoles && localStorage.getItem('organization')) {
    const organization = JSON.parse(localStorage.getItem('organization'));
    // this.store.dispatch(new App.Actions.SetCurrentOrganization(organization));

    if (localStorage.getItem('user')) {
      const user = JSON.parse(localStorage.getItem('user')) as IMemberResult;

      if (user.organizations) {
        const organizations = user.organizations.map((org) => {
          return {
            address: org.organization['address'],
            name: org.organization['name'],
            id: org.organization['_id'],
            subscriptionStatus: org.organization.subscriptionStatus,
            organizationId: org.organization['_id'],
            roles: org.organization.roles,
            url: org.organization['url'],
            email: org.organization['email'],
            phone: org.organization['phone'],
            description: org.organization['description'],
            logo: org.organization['logo'],
            tags: org.organization['tags'],
          } as IOrganizationBase;
        });
        this.appRoles = organization.roles;
        if (!user.currentOrganization)
          user.currentOrganization = user.organizations[0];
        this.store.dispatch(
          new App.Actions.SetCurrentOrganization(user.currentOrganization),
        );
        this.userRoles = user.currentOrganization.roles;
        this.store.dispatch(new App.Actions.SetOrganizations(organizations));
        this.store.dispatch(new User.Actions.SetLoginInformation(user));
        this.store.dispatch(new App.Actions.SetRoles());
      }
    }
    // }
  }
}
